import { NotificationBlastContact } from 'messages/hooks/useMessageOverview'
import { useMessageRecipientRowStyles } from 'messages/hooks/useStyles'
import HourglassFullTwoToneIcon from '@material-ui/icons/HourglassFullTwoTone'
import PhoneIcon from '@material-ui/icons/Phone'
import AlternateEmailIcon from '@material-ui/icons/AlternateEmail'
import FeedbackOutlinedIcon from '@material-ui/icons/FeedbackOutlined'
import cn from 'classnames'
import CloseIcon from '@material-ui/icons/Close'
import React from 'react'

interface RecipientRowProps {
  contact: NotificationBlastContact
  emailsOnly: boolean
  smsOnly: boolean
  onRemove: () => void
}

const isBounced = (
  { isEmailBounced, isPhoneBounced }: NotificationBlastContact,
  { emailsOnly, smsOnly }: { emailsOnly: boolean; smsOnly: boolean },
): boolean => {
  if (emailsOnly) return isEmailBounced
  if (smsOnly) return isPhoneBounced
  return isEmailBounced || isPhoneBounced
}

const RecipientRowIcon = ({
  contact,
  emailsOnly,
  smsOnly,
}: {
  contact: NotificationBlastContact
  emailsOnly: boolean
  smsOnly: boolean
}) => {
  const classes = useMessageRecipientRowStyles()
  if (contact.loading) {
    return <HourglassFullTwoToneIcon className={classes.rowIcon} />
  }
  if (!contact.doNotContact) {
    if (smsOnly && contact.phoneNumber) {
      return <PhoneIcon className={classes.rowIcon} />
    }
    if (contact.sendToPhone && !emailsOnly) {
      return <PhoneIcon className={classes.rowIcon} />
    } else if (contact.email && !smsOnly) {
      return <AlternateEmailIcon className={classes.rowIcon} />
    }
  }
  return <FeedbackOutlinedIcon className={cn(classes.rowIcon, classes.error)} />
}

export const RecipientRow = ({ contact, emailsOnly, smsOnly, onRemove }: RecipientRowProps) => {
  const classes = useMessageRecipientRowStyles()
  return (
    <div className={classes.recipientRow}>
      <div className={classes.recipientContainer}>
        <RecipientRowIcon contact={contact} emailsOnly={emailsOnly} smsOnly={smsOnly} />{' '}
        <div className={classes.recipientNameContainer}>
          <div className={classes.recipientName}>
            {contact.firstName} {contact.lastName}
          </div>
          {contact.loading ? null : <RecipientErrorInfo contact={contact} emailsOnly={emailsOnly} smsOnly={smsOnly} />}
        </div>
      </div>
      <CloseIcon className={classes.closeIcon} onClick={onRemove} />
    </div>
  )
}

const RecipientErrorInfo = ({
  contact,
  emailsOnly,
  smsOnly,
}: {
  contact: NotificationBlastContact
  emailsOnly: boolean
  smsOnly: boolean
}) => {
  const classes = useMessageRecipientRowStyles()

  if (contactHasError({ contact, emailsOnly, smsOnly })) {
    return <div className={classes.recipientErrorInfo}>Invalid: {getReason({ contact, emailsOnly, smsOnly })}</div>
  }

  return null
}

const contactHasError = ({
  contact,
  emailsOnly,
  smsOnly,
}: {
  contact: NotificationBlastContact
  emailsOnly: boolean
  smsOnly: boolean
}): boolean => {
  if (contact.doNotContact || contact.failureReason) {
    return true
  }
  if (smsOnly) {
    return !contact.phoneNumber || contact.isPhoneBounced
  }
  if (emailsOnly) {
    return !contact.email || contact.isEmailBounced
  }
  if (contact.email) {
    return false
  }
  return !contact.sendToPhone
}

const getReason = ({
  contact,
  emailsOnly,
  smsOnly,
}: {
  contact: NotificationBlastContact
  emailsOnly: boolean
  smsOnly: boolean
}) => {
  if (contact.failureReason === 'unsubscribed') {
    return 'Unsubscribed'
  }
  if (contact.doNotContact) {
    return 'Do Not Contact'
  }
  if (isBounced(contact, { emailsOnly, smsOnly })) {
    return 'Bounced'
  }
  return 'No Contact Information on File'
}
